import { useState, useRef, FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, ImagePlus, X } from "lucide-react";

import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { Alert } from "../../components/Alert/Alert";
import { Modal } from "../../components/Modal/Modal";
import { SegmentedToggle } from "../../components/SegmentedToggle/SegmentedToggle";
import { gradientFor, initialsFor } from "../../components/Avatar/avatarLook";
import { ApiError, describeError } from "../../lib/errors";
import { createProject, CreateProjectResponse } from "./api";
import styles from "./CreateProjectModal.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (r: CreateProjectResponse) => void;
}

// Validation rules — mockup L831: 2–60 chars, letters/digits/space/dash.
const NAME_PATTERN = /^[A-Za-z0-9 -]+$/;
const MAX_ICON_BYTES = 50 * 1024; // 50 KB
const ACCEPTED_MIME = ["image/png", "image/jpeg", "image/webp"];

type IconMode = "auto" | "upload";

/**
 * A closed modal mounts nothing at all, so the form starts blank on every open
 * — the seven pieces of state below are born fresh instead of being reset by
 * an effect one render after the dialog is already on screen.
 */
export function CreateProjectModal({ open, onClose, onCreated }: Props) {
  if (!open) return null;
  return <CreateProjectForm onClose={onClose} onCreated={onCreated} />;
}

function CreateProjectForm({ onClose, onCreated }: Omit<Props, "open">) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [iconMode, setIconMode] = useState<IconMode>("auto");
  const [iconDataUrl, setIconDataUrl] = useState<string | null>(null);
  const [iconError, setIconError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function validateName(value: string): string | null {
    const trimmed = value.trim();
    if (trimmed.length < 2 || trimmed.length > 60) {
      return "Name must be 2–80 characters.";
    }
    if (!NAME_PATTERN.test(trimmed)) {
      return "Use letters, numbers, spaces, or hyphens only.";
    }
    return null;
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    setIconError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_MIME.includes(file.type)) {
      setIconError("Only PNG, JPG, or WebP images are allowed.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_ICON_BYTES) {
      setIconError("Image must be 50 KB or smaller.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") setIconDataUrl(result);
    };
    reader.onerror = () => {
      setIconError("Failed to read the image. Try again.");
    };
    reader.readAsDataURL(file);
  }

  function removeIcon() {
    setIconDataUrl(null);
    setIconError(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function submit() {
    setSubmitError(null);
    const trimmed = name.trim();
    const err = validateName(trimmed);
    if (err) {
      setNameError(err);
      return;
    }
    setNameError(null);
    if (iconMode === "upload" && !iconDataUrl) {
      setIconError("Choose an image or switch back to Auto.");
      return;
    }
    setSubmitting(true);
    try {
      // The server expects a raw base64 string (no `data:` prefix). Strip it
      // before sending so the payload matches the existing API contract.
      const stripped = iconDataUrl?.replace(/^data:[^;]+;base64,/, "");
      const r = await createProject({
        name: trimmed,
        ...(iconMode === "upload" && stripped
          ? { icon_base64: stripped }
          : {}),
      });
      await qc.invalidateQueries({ queryKey: ["projects"] });
      onCreated(r);
    } catch (err) {
      setSubmitError(
        err instanceof ApiError
          ? describeError(err.code)
          : describeError("internal"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  const previewName = name.trim() || "Project";
  const previewInitial = initialsFor(previewName).slice(0, 1);

  return (
    <Modal
      open
      onClose={onClose}
      title="Create new project"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={submitting}
            icon={<Plus size={14} />}
          >
            {submitting ? "Creating…" : "Create project"}
          </Button>
        </>
      }
    >
      {/*
       * The default Modal max-width is 400px. The Create form needs more room
       * for the icon-edit row, so we widen it via a sibling CSS rule that
       * targets the dialog ancestor by aria-label (matches Modal's title).
       */}
      <div className={styles.widthOverride} aria-hidden="true" />
      <form onSubmit={onSubmit} className={styles.form}>
        <p className={styles.lead}>
          A project is a separate tenant on the platform — its own players,
          guilds, chat, config, and API key.
        </p>

        {submitError && <Alert variant="danger">{submitError}</Alert>}

        <Input
          label="Project name"
          autoFocus
          value={name}
          placeholder="e.g. Pixel Saga"
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(null);
          }}
          hintRight={
            <span className={styles.labelHint}>
              Visible across the admin UI
            </span>
          }
          hint={
            nameError
              ? undefined
              : "Letters, numbers, spaces and dashes. Can be renamed later."
          }
          error={nameError ?? undefined}
          required
        />

        <div className={styles.iconField}>
          <div className={styles.fieldLabelRow}>
            <span className={styles.fieldLabel}>Project icon</span>
            <span className={styles.labelHint}>
              Auto-generated or upload custom
            </span>
          </div>
          <div className={styles.iconEdit}>
            <div className={styles.iconPreview}>
              {iconMode === "upload" && iconDataUrl ? (
                <img
                  src={iconDataUrl}
                  alt=""
                  className={styles.iconBig}
                  width={56}
                  height={56}
                />
              ) : (
                <span
                  className={styles.iconBig}
                  aria-hidden="true"
                  style={{ background: gradientFor(previewName) }}
                >
                  {previewInitial}
                </span>
              )}
            </div>
            <div className={styles.iconActions}>
              <SegmentedToggle
                aria-label="Icon source"
                options={[
                  { value: "auto", label: "Auto from name" },
                  { value: "upload", label: "Upload" },
                ]}
                value={iconMode}
                onChange={(v) => {
                  setIconMode(v);
                  setIconError(null);
                }}
              />
              {iconMode === "upload" && (
                <div className={styles.uploadRow}>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={onPickFile}
                    className={styles.fileInput}
                    aria-label="Upload icon image"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => fileRef.current?.click()}
                    icon={<ImagePlus size={12} />}
                  >
                    {iconDataUrl ? "Replace image" : "Upload image"}
                  </Button>
                  {iconDataUrl && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={removeIcon}
                      icon={<X size={12} />}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              )}
              {iconError ? (
                <div className={styles.iconError}>{iconError}</div>
              ) : (
                <div className={styles.iconHint}>
                  PNG, JPG, WebP · square · max 50 KB · 128×128 recommended
                </div>
              )}
            </div>
          </div>
        </div>

        <Alert variant="info">
          An API key will be generated automatically. You'll see it once on
          the next screen — save it before closing.
        </Alert>
      </form>
    </Modal>
  );
}
